import { Box, InputAdornment, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { usePanelTokens } from "../../utils/panelTokens";

/**
 * Search, filters and a card/table switch for a public dataset page.
 *
 * Lifted out of `motorData/MotorDataIndex`, which is where this markup already
 * lived — the catalog, the log gallery and the downloads list all wanted the
 * same row and were about to grow three copies of it.
 *
 * Not `Templates/NewListComponent`'s built-in search: that one is app chrome
 * (checkboxes, pagination, an Add button, a `Header`) and these pages wear
 * `PublicShell`, which deliberately carries none of it.
 *
 * Filters come in as `children` rather than as a config prop, because their
 * shape is the one thing that genuinely differs: a handful of vehicle types
 * reads well as chips, forty motor brands only ever works as a select.
 */
const CollectionToolbar = ({ query, onQueryChange, placeholder = "Search…", view, onViewChange, children }) => {
  const t = usePanelTokens();
  const field = {
    "& .MuiOutlinedInput-root": { borderRadius: 2, background: t.surface, color: t.text },
    "& fieldset": { borderColor: t.border },
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        size="small"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: t.textFaint }} />
            </InputAdornment>
          ),
        }}
        sx={{ width: { xs: "100%", sm: 320 }, ...field }}
      />

      {children}

      {/* Pushed to the far end, and only when the page offers both views. */}
      {onViewChange && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(e, next) => next && onViewChange(next)}
          sx={{ ml: { sm: "auto" }, "& .MuiToggleButton-root": { color: t.textFaint, borderColor: t.border } }}
        >
          <ToggleButton value="cards" aria-label="Card view">
            <GridViewOutlinedIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <TableRowsOutlinedIcon sx={{ fontSize: 18 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );
};

/** A low-cardinality filter: every option visible, one click to clear. */
export const FilterChips = ({ value, onChange, options, allLabel = "All" }) => {
  const t = usePanelTokens();
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      // `next` is null when the active button is clicked again; treating that as
      // "back to all" is what makes a chip row clearable without a reset button.
      onChange={(e, next) => onChange(next ?? "all")}
      sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, color: t.textFaint, borderColor: t.border } }}
    >
      <ToggleButton value="all">{allLabel}</ToggleButton>
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

/** A high-cardinality filter, where showing every option would be a wall. */
export const FilterSelect = ({ value, onChange, options, allLabel = "All", width = 200 }) => {
  const t = usePanelTokens();
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        width: { xs: "100%", sm: width },
        "& .MuiOutlinedInput-root": { borderRadius: 2, background: t.surface, color: t.text },
        "& fieldset": { borderColor: t.border },
      }}
    >
      <MenuItem value="all">{allLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default CollectionToolbar;
